/**
 * RTWO Indexer Engine — Transaction Indexing Service
 */

import { INDEXER_SERVICE_IDS } from '../domain-models';
import type { IndexedTransactionId, RTWOIndexedTransaction } from '../domain-models';
import type { ITransactionIndexingService, TransactionIndexResult } from '../service-interfaces';
import type { IndexerRepositories } from '../repositories/indexer-repositories';
import type { LedgerAdapterRegistry } from '../registry/adapter-registries';
import { IndexerInternalEventTypes } from '../events/indexer-event-bus';
import { BaseIndexerService, type IndexerServiceContext } from './base-indexer-service';

export class TransactionIndexingService
  extends BaseIndexerService
  implements ITransactionIndexingService
{
  readonly serviceId = INDEXER_SERVICE_IDS.TRANSACTION_INDEXING;
  readonly serviceName = 'Transaction Indexing Service';
  readonly description = 'Indexes transactions via Ledger adapter. No fake transactions indexed.';
  readonly integrations = ['RPC Platform', 'Ledger Engine', 'Block Indexing Service'];
  readonly requiresRPCSource = true;
  readonly requiresIndexStorage = false;

  constructor(
    context: IndexerServiceContext,
    private readonly repositories: IndexerRepositories,
    private readonly ledgerRegistry: LedgerAdapterRegistry
  ) {
    super(context);
  }

  async indexTransaction(transactionHash: string): Promise<TransactionIndexResult> {
    if (!this.ledgerRegistry.isConfigured()) {
      return {
        success: false,
        indexedTransactionId: null,
        error:
          'RPC Platform Not Connected — Cannot index transactions until real RPC source is connected.',
        isPlaceholder: true,
        source: 'Indexer Engine — RPC Platform Not Connected',
      };
    }

    const adapterResult = await this.ledgerRegistry
      .getActiveAdapter()
      .indexTransaction(transactionHash);

    if (adapterResult?.transaction) {
      this.repositories.transactions.save(adapterResult.transaction);
      this.context.eventBus.emit(IndexerInternalEventTypes.TRANSACTION_INDEXED, {
        indexedTransactionId: adapterResult.transaction.indexedTransactionId,
        transactionHash,
      });
    }

    return {
      success: false,
      indexedTransactionId: null,
      error:
        'RPC Platform Not Connected — Cannot index transactions until real RPC source is connected.',
      isPlaceholder: true,
      source: 'Indexer Engine — RPC Platform Not Connected',
    };
  }

  getAllIndexedTransactions(): RTWOIndexedTransaction[] {
    if (!this.ledgerRegistry.isConfigured()) {
      return [];
    }

    return this.repositories.transactions.findAll();
  }

  getIndexedTransaction(indexedTransactionId: IndexedTransactionId): RTWOIndexedTransaction | null {
    if (!this.ledgerRegistry.isConfigured()) {
      return null;
    }

    return this.repositories.transactions.findById(indexedTransactionId);
  }

  getIndexedTransactionCount(): 0 {
    return 0;
  }
}
